import React, { useMemo, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import SpecialContentSidebar from 'components/shared/SpecialContentSidebar';

import ChooseRoomModal from 'components/features/WallPreview/ChooseRoomModal';
import EditRoomUploadsModal from 'components/features/WallPreview/EditRoomUploadsModal';
import WallPreviewTools from 'components/features/WallPreview/WallPreviewTools';
import ManageWallPreviewsModal from 'components/features/WallPreview/ManageWallPreviewsModal';
import ManageArrangementsModal from 'components/features/Arrangements/ManageArrangementsModal';
import CustomArrangementsTools from 'components/features/Arrangements/CustomArrangementsTools';
import UnavailableArrangementModal from 'components/features/Arrangements/UnavailableArrangementModal';

import { useAppDispatch, useAppSelector } from 'utils/hooks';
import { imageFilters } from 'utils/image_util';
import { renderModal } from 'utils/modal_util';

import { prepareRestoreArrangement } from 'thunks/arrangement_thunks';

import crops from 'domains/crops';
import session from 'domains/session';
import ui from 'domains/ui';
import wallPreviewModule from 'domains/wall_previews';
import roomsModule from 'domains/wall_preview_rooms';
import activeEditor from 'domains/active_editor';
import savedArrangements from 'domains/saved_arrangements';

import SettingsMenu from './SettingsMenu';

import type { PickerResponse } from 'filestack-js';
import type { WosIconProp } from 'types/common_types';
import type { WallPreviewRoom, WallPreviewUserRoom } from 'domains/wall_preview_rooms';

function EditorSidebar() {
  const dispatch = useAppDispatch();
  const isLoggedIn = useAppSelector(session.selectors.isLoggedIn);
  const allSavedArrangements = useAppSelector(savedArrangements.selectors.selectAll);
  const activeArrangementId = useAppSelector(savedArrangements.selectors.activeId);
  const wallPreviews = useAppSelector(wallPreviewModule.selectors.selectAll);
  const rooms = useAppSelector(roomsModule.selectors.selectAll);
  const whichWallPreview = useAppSelector(roomsModule.selectors.whichWallPreview);
  const activeSubcatalog = useAppSelector(activeEditor.selectors.subcatalog);
  const activePhoto = useAppSelector(crops.selectors.activeNodePhoto);
  const activeCrop = useAppSelector(crops.selectors.activeCrop);
  const activeSpecialContentSidebarMenu = useAppSelector(ui.selectors.activeSidebarMenu);
  const unavailableArrangement = useAppSelector(savedArrangements.selectors.unavailable);
  const unavailableArrangementIds = useAppSelector(savedArrangements.selectors.unavailableIds);
  const hasMobileNavigation = useAppSelector(ui.selectors.hasMobileNavigation);

  const [showChooseRoomModal, setShowChooseRoomModal] = useState(false);
  const [showEditRoomModal, setShowEditRoomModal] = useState(false);
  const [showManageWallPreviewsModal, setShowManageWallPreviewsModal] = useState(false);

  const [showUnavailableArrangementModal, setShowUnavailableArrangementModal] = useState(false);
  const [showManageArrangementsModal, setShowManageArrangementsModal] = useState(false);

  useEffect(() => {
    if (unavailableArrangement) {
      setShowUnavailableArrangementModal(true);
    }
  }, [unavailableArrangement]);

  function loadWallPreviews() {
    dispatch(wallPreviewModule.actions.getAll.pending());
  }

  function loadUserRooms() {
    if (isLoggedIn) {
      dispatch(roomsModule.actions.fetchUserRooms.pending());
    }
  }

  function loadArrangements() {
    if (isLoggedIn) {
      dispatch(savedArrangements.actions.getAll.pending());
    }
  }

  function handleSelectPreview(room: WallPreviewRoom) {
    dispatch(roomsModule.actions.setWhichWallPreview(room));
    setShowChooseRoomModal(false);
  }

  function handleManageArrangements() {
    setShowManageArrangementsModal(true);
  }

  function handleManageWallPreviews() {
    setShowManageWallPreviewsModal(true);
  }

  function handleChangeRoom() {
    setShowChooseRoomModal(true);
  }

  function closeEditRoomModal() {
    setShowEditRoomModal(false);
    dispatch(roomsModule.actions.resetEditDrafts());
  }

  function handleEditRoom(room: WallPreviewUserRoom) {
    dispatch(roomsModule.actions.createRoomEditDraft(room.id));
    setShowEditRoomModal(true);
  }

  function handleFilestackUploadDone(blobs: PickerResponse) {
    const roomUploads = blobs.filesUploaded.map(({ url, filename }) => {
      return { url, filename, wall_size: 120 };
    });

    dispatch(roomsModule.actions.createUserRooms.pending(roomUploads));
    setShowEditRoomModal(true);
  }

  const arrangementsItems = useMemo(() => {
    return allSavedArrangements.map((arrangement) => {
      const { id, preview_url: previewURL, name } = arrangement;
      const label = unavailableArrangementIds.find((unavailable) => unavailable === id) ? (
        <span>
          {name} <br />
          <span className="error-message">
            <FontAwesomeIcon icon="exclamation-circle" /> Unavailable
          </span>
        </span>
      ) : (
        name
      );
      return {
        id,
        label,
        image: previewURL,
        pendingImage: previewURL === '',
        selected: activeArrangementId === arrangement.id,
        handleSelectItem() {
          dispatch(prepareRestoreArrangement(arrangement));
        },
      };
    });
  }, [dispatch, allSavedArrangements, activeArrangementId, unavailableArrangementIds]);

  const roomItems = useMemo(() => {
    return rooms.map((room) => {
      const { id, description, previewSrc } = room;
      return {
        id,
        label: description,
        image: previewSrc,
        selected: whichWallPreview?.id === room.id,
        handleSelectItem() {
          dispatch(roomsModule.actions.setWhichWallPreview(room));
        },
      };
    });
  }, [dispatch, rooms, whichWallPreview]);

  const wallPreviewsItems = useMemo(() => {
    return wallPreviews.map((preview) => {
      const { id, url, name } = preview;
      return {
        id,
        image: url || '',
        pendingImage: !url,
        label: name,
        handleSelectItem() {
          window.open(url, '_blank');
        },
      };
    });
  }, [wallPreviews]);

  const imageFiltersItems = useMemo(() => {
    if (!activeCrop || !activePhoto) return [];

    return imageFilters.map((filter) => {
      const { id: photoId } = activePhoto;
      const { filter: selectedFilter } = activeCrop;
      const { filterType, title, filePathThumb } = filter;

      return {
        id: `${photoId}-${filterType}`,
        label: title,
        image: activePhoto[filePathThumb],
        selected: selectedFilter === filterType,
        handleSelectItem() {
          dispatch(crops.actions.filterCrop({ filter, crop: activeCrop }));
        },
      };
    });
  }, [activeCrop, activePhoto, dispatch]);

  const topMenuGroup = [
    {
      id: 'arrangements',
      primaryGroup: [
        {
          id: 'arrangements',
          triggerIcon: 'arrangements' as WosIconProp,
          disabled: !isLoggedIn,
          index: 0,
          onOpen: loadArrangements,
          menuProps: {
            title: 'Saved Arrangements',
            items: arrangementsItems,
            handleDone: () => {},
            handleViewManage: () => {
              handleManageArrangements();
            },
          },
        },
      ],
      secondaryGroup: !hasMobileNavigation ? <CustomArrangementsTools /> : null,
    },
    {
      id: 'wall-previews',
      primaryGroup: [
        {
          id: 'rooms',
          triggerIcon: 'wallPreview' as WosIconProp,
          disabled: !!activeSubcatalog && !activeSubcatalog.wall_display,
          onOpen: loadUserRooms,
          index: 0,
          menuProps: {
            title: 'Walls',
            items: roomItems,
            handleDone: () => {},
            handleViewManage: () => {
              handleChangeRoom();
            },
          },
        },
        {
          id: 'saved-wall-previews',
          triggerIcon: 'wallPreviewSaved' as WosIconProp,
          disabled: !isLoggedIn,
          onOpen: loadWallPreviews,
          index: 1,
          menuProps: {
            title: 'Saved Wall Previews',
            items: wallPreviewsItems,
            handleDone: () => {},
            handleViewManage: () => {
              handleManageWallPreviews();
            },
          },
        },
      ],
      secondaryGroup: <WallPreviewTools />,
    },
    {
      id: 'image-filters',
      primaryGroup: [
        {
          id: 'image-filters',
          triggerIcon: 'wand-magic-sparkles' as WosIconProp,
          disabled: !activeCrop,
          onOpen: () => {},
          index: 0,
          menuProps: {
            title: 'Image Filters',
            items: imageFiltersItems,
            handleDone: () => {},
          },
        },
      ],
    },
  ];

  const bottomMenuItems = useMemo(() => {
    return [
      {
        id: 'settings-menu',
        el: <SettingsMenu />,
      },
    ];
  }, []);

  return (
    <React.Fragment>
      <div className="editor-sidebar">
        <SpecialContentSidebar
          activeMenu={activeSpecialContentSidebarMenu}
          setActiveMenu={(menu) => {
            dispatch(ui.actions.setActiveContentSidebarMenu(menu));
          }}
          containerPadding={hasMobileNavigation ? 0 : 30}
          topMenuGroup={topMenuGroup}
          bottomMenuItems={bottomMenuItems}
        />
      </div>

      {showChooseRoomModal &&
        renderModal(
          <ChooseRoomModal
            lastFocusedElementId="content-menu-trigger-rooms"
            onClose={() => setShowChooseRoomModal(false)}
            onSelectPreview={handleSelectPreview}
            onFilestackUploadDone={handleFilestackUploadDone}
            onEditRoom={handleEditRoom}
          />
        )}

      {showEditRoomModal &&
        renderModal(
          <EditRoomUploadsModal
            handleChangeRoom={handleChangeRoom}
            lastFocusedElementId="content-menu-trigger-rooms"
            onClose={closeEditRoomModal}
          />
        )}

      {showManageWallPreviewsModal &&
        renderModal(
          <ManageWallPreviewsModal onClose={() => setShowManageWallPreviewsModal(false)} />
        )}

      {showUnavailableArrangementModal &&
        !!unavailableArrangement &&
        renderModal(
          <UnavailableArrangementModal
            id={unavailableArrangement.id}
            items={unavailableArrangement.items}
            onClose={() => setShowUnavailableArrangementModal(false)}
          />
        )}

      {showManageArrangementsModal &&
        renderModal(
          <ManageArrangementsModal onClose={() => setShowManageArrangementsModal(false)} />
        )}
    </React.Fragment>
  );
}

export default EditorSidebar;
